import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  IconButton,
  Autocomplete,
  TextField,
  Card,
  CardContent,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import BudgetProgressBarMini from '../components/BudgetProgressBarMini';

interface Risorsa {
  id: number;
  nome: string;
  cognome: string;
  struttura: string;
}

interface Progetto {
  id: number;
  titolo: string;
}

interface RiepilogoProgetto {
  id_progetto: number;
  titolo: string;
  cliente: string;
  pm: string;
  budget_gg_uu: number | null;
  budget_pianificato_gg_uu: number | null;
  budget_disponibile_gg_uu: number | null;
  totale_giorni_uomo: number | null;
  budget_costo_totale: number | null;
  totale_costo: number | null;
  costo_disponibile: number | null;
}

interface DettaglioAllocazione {
  id_progetto: number;
  progetto_titolo: string;
  cliente: string;
  pm: string;
  id_risorsa: number;
  nome: string;
  cognome: string;
  struttura: string;
  costo_orario: number | null;
  mese: number;
  percentuale: number;
  ore_allocate: number | null;
  giorni_uomo: number | null;
  costo: number | null;
}

interface EconomicsData {
  riepilogo: RiepilogoProgetto[];
  dettaglio: DettaglioAllocazione[];
  totale_generale: {
    totale_budget_gg_uu: number;
    totale_budget_costo: number;
    totale_giorni_uomo: number;
    totale_costo: number;
    totale_gg_uu_da_pianificare: number;
    totale_costo_da_pianificare: number;
  };
  anno: number;
}

const MESI_LABELS: { [key: number]: string } = {
  1: 'GEN', 2: 'FEB', 3: 'MAR', 4: 'APR', 5: 'MAG', 6: 'GIU',
  7: 'LUG', 8: 'AGO', 9: 'SET', 10: 'OTT', 11: 'NOV', 12: 'DIC'
};

const EconomicsPage: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [economicsData, setEconomicsData] = useState<EconomicsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [risorse, setRisorse] = useState<Risorsa[]>([]);
  const [progetti, setProgetti] = useState<Progetto[]>([]);
  const [progettiOptions, setProgettiOptions] = useState<Progetto[]>([]);
  const [loadingProgetti, setLoadingProgetti] = useState(false);
  const [selectedRisorse, setSelectedRisorse] = useState<number[]>([]);
  const [selectedStrutture, setSelectedStrutture] = useState<string[]>([]);
  const [selectedMesi, setSelectedMesi] = useState<number[]>(Array.from({ length: 12 }, (_, i) => i + 1));
  const [selectedProgetti, setSelectedProgetti] = useState<number[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const { user } = useAuth();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  const allMesi = Array.from({ length: 12 }, (_, i) => i + 1);

  // Estrae le strutture uniche dalle risorse
  const strutture = Array.from(new Set(risorse.map(r => r.struttura).filter(s => s && s.trim() !== '')));

  // Filtra le risorse in base alle strutture selezionate
  // Se nessuna struttura è selezionata, non mostrare nessuna risorsa
  const risorseFiltrate = selectedStrutture.length > 0
    ? risorse.filter(r => selectedStrutture.includes(r.struttura))
    : [];

  // Carica risorse
  useEffect(() => {
    loadRisorse();
  }, []);

  // Inizializza mesi di default - tutti i mesi dell'anno corrente
  useEffect(() => {
    setSelectedMesi(allMesi);
  }, [selectedYear]);

  // Aggiorna le risorse selezionate quando cambiano le strutture
  useEffect(() => {
    if (risorse.length === 0) return; // Non fare nulla se le risorse non sono ancora caricate

    // Se nessuna struttura è selezionata, nessuna risorsa selezionata
    const risorseFiltrateIds = selectedStrutture.length > 0
      ? risorse.filter(r => selectedStrutture.includes(r.struttura)).map(r => r.id)
      : [];

    setSelectedRisorse(risorseFiltrateIds);
  }, [selectedStrutture, risorse]);

  // Carica dati quando cambiano i filtri
  useEffect(() => {
    if (selectedRisorse.length > 0 && selectedMesi.length > 0) {
      loadEconomicsData();
    } else if (selectedMesi.length === 0 || selectedRisorse.length === 0) {
      setEconomicsData(null);
    }
  }, [selectedYear, selectedRisorse, selectedMesi, selectedProgetti]);

  const loadRisorse = async () => {
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/risorse`, { headers });
      const data = await response.json();
      // Assicurati che data sia un array
      const risorseArray = Array.isArray(data) ? data : [];
      setRisorse(risorseArray);

      // Inizializza tutte le strutture come selezionate (solo al primo caricamento)
      const struttureUniche = Array.from(new Set(risorseArray.map((r: Risorsa) => r.struttura).filter((s: string) => s && s.trim() !== '')));
      setSelectedStrutture(struttureUniche);

      // Non settare selectedRisorse qui - sarà gestito dall'useEffect
    } catch (err) {
      console.error('Errore nel caricamento delle risorse:', err);
      setRisorse([]);
    }
  };

  const searchProgetti = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      setProgettiOptions([]);
      return;
    }

    setLoadingProgetti(true);
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      // Ricerca progetti con termine di ricerca, limitato a 50 risultati
      const response = await fetch(`${BASE_URL}/progetti?limit=50&includeDisabled=false&search=${encodeURIComponent(searchTerm)}`, { headers });
      const result = await response.json();
      // L'endpoint restituisce { data: [...], pagination: {...} }
      const progettiArray = result.data && Array.isArray(result.data) ? result.data : [];
      setProgettiOptions(progettiArray);
    } catch (err) {
      console.error('Errore nella ricerca dei progetti:', err);
      setProgettiOptions([]);
    } finally {
      setLoadingProgetti(false);
    }
  };

  const loadEconomicsData = async () => {
    setLoading(true);
    setError(null);

    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const risorseParam = selectedRisorse.join(',');
      const mesiParam = selectedMesi.join(',');
      const progettiParam = selectedProgetti.length > 0 ? `&progetti=${selectedProgetti.join(',')}` : '';

      const url = `${BASE_URL}/economics/${selectedYear}?risorse=${risorseParam}&mesi=${mesiParam}${progettiParam}`;
      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error('Errore nel caricamento dei dati');
      }

      const data = await response.json();
      setEconomicsData(data);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento dei dati economici');
    } finally {
      setLoading(false);
    }
  };

  const handleRisorseChange = (event: any) => {
    const value = event.target.value;
    setSelectedRisorse(typeof value === 'string' ? value.split(',').map(Number) : value);
  };

  const toggleRow = (progettoId: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(progettoId)) {
        next.delete(progettoId);
      } else {
        next.add(progettoId);
      }
      return next;
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const handleRicalcolaPianificati = async () => {
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/economics/ricalcola-residui`, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        throw new Error('Errore nel ricalcolo dei budget pianificati');
      }

      const data = await response.json();
      alert(`Budget pianificati ricalcolati con successo! ${data.progetti_aggiornati} progetti aggiornati.`);

      // Ricarica i dati economics
      if (selectedRisorse.length > 0 && selectedMesi.length > 0) {
        loadEconomicsData();
      }
    } catch (err: any) {
      alert(err.message || 'Errore nel ricalcolo dei budget pianificati');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" fontWeight="bold">
          Analisi Economica
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleRicalcolaPianificati}
          sx={{ textTransform: 'none' }}
        >
          Ricalcola Budget Pianificati
        </Button>
      </Box>

      {/* Filtri */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box>
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel>Strutture</InputLabel>
              <Select
                multiple
                value={selectedStrutture}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedStrutture(typeof value === 'string' ? value.split(',') : value);
                }}
                input={<OutlinedInput label="Strutture" />}
                renderValue={(selected) => `${selected.length} strutture selezionate`}
              >
                {strutture.map((struttura) => (
                  <MenuItem key={struttura} value={struttura}>
                    <Checkbox checked={selectedStrutture.indexOf(struttura) > -1} />
                    <ListItemText primary={struttura} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedStrutture(strutture)}
                sx={{ textTransform: 'none' }}
              >
                Seleziona tutti
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedStrutture([])}
                sx={{ textTransform: 'none' }}
              >
                Deseleziona tutti
              </Button>
            </Box>
          </Box>

          <Box>
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel>Risorse</InputLabel>
              <Select
                multiple
                value={selectedRisorse}
                onChange={handleRisorseChange}
                input={<OutlinedInput label="Risorse" />}
                renderValue={(selected) => `${selected.length} risorse selezionate`}
              >
                {risorseFiltrate.map((risorsa) => (
                  <MenuItem key={risorsa.id} value={risorsa.id}>
                    <Checkbox checked={selectedRisorse.indexOf(risorsa.id) > -1} />
                    <ListItemText primary={`${risorsa.cognome} ${risorsa.nome}`} secondary={risorsa.struttura} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedRisorse(risorseFiltrate.map(r => r.id))}
                sx={{ textTransform: 'none' }}
              >
                Seleziona tutti
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedRisorse([])}
                sx={{ textTransform: 'none' }}
              >
                Deseleziona tutti
              </Button>
            </Box>
          </Box>

          <Box>
            <Autocomplete
              multiple
              sx={{ minWidth: 300 }}
              options={allMesi}
              getOptionLabel={(option) => MESI_LABELS[option]}
              value={selectedMesi}
              onChange={(_, newValue) => {
                setSelectedMesi(newValue);
              }}
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {MESI_LABELS[option]}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Mesi"
                  placeholder={selectedMesi.length === 0 ? "Cerca mesi..." : ""}
                />
              )}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedMesi(allMesi)}
                sx={{ textTransform: 'none' }}
              >
                Seleziona tutti
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedMesi([])}
                sx={{ textTransform: 'none' }}
              >
                Deseleziona tutti
              </Button>
            </Box>
          </Box>

          <Box>
            <Autocomplete
              multiple
              sx={{ minWidth: 300 }}
              options={progettiOptions}
              getOptionLabel={(option) => option.titolo}
              value={progetti}
              onChange={(_, newValue) => {
                setProgetti(newValue);
                setSelectedProgetti(newValue.map(p => p.id));
              }}
              onInputChange={(_, value) => {
                searchProgetti(value);
              }}
              loading={loadingProgetti}
              disableCloseOnSelect
              filterOptions={(x) => x}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.titolo}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Progetti (opzionale)"
                  placeholder="Cerca progetti (min 2 caratteri)..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProgetti ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={loadingProgetti ? "Caricamento..." : "Nessun progetto trovato. Digita per cercare..."}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setProgetti([]);
                  setSelectedProgetti([]);
                }}
                sx={{ textTransform: 'none' }}
              >
                Rimuovi tutti
              </Button>
            </Box>
          </Box>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Anno</InputLabel>
            <Select
              value={selectedYear}
              label="Anno"
              onChange={(e) => setSelectedYear(e.target.value as number)}
            >
              {years.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {!economicsData && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <Alert severity="info" sx={{ maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              Nessun dato da visualizzare
            </Typography>
            <Typography variant="body2">
              Seleziona almeno una risorsa e un mese per visualizzare l'analisi economica.
            </Typography>
          </Alert>
        </Box>
      )}

      {economicsData && (
        <>
          {/* Totale Generale */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
              Riepilogo Generale
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {/* Prima riga: Budget, Pianificato, Da pianificare (gg/uu) */}
              <Card sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 250, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    Totale budget gg/uu
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                    {economicsData.totale_generale.totale_budget_gg_uu.toFixed(2)} gg
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 250, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    Totale Giorni Uomo pianificati
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                    {economicsData.totale_generale.totale_giorni_uomo.toFixed(2)} gg
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 250, background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    Totale gg/uu da pianificare
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                    {economicsData.totale_generale.totale_gg_uu_da_pianificare.toFixed(2)} gg
                  </Typography>
                </CardContent>
              </Card>

              {/* Seconda riga: Budget costo, Costo pianificato, Costi da pianificare */}
              <Card sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 250, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    Totale costo budget
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                    {formatCurrency(economicsData.totale_generale.totale_budget_costo)}
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 250, background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    Totale Costo pianificato
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                    {formatCurrency(economicsData.totale_generale.totale_costo)}
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 250, background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ color: 'rgba(80,80,80,0.9)', mb: 1 }}>
                    Totale costi da pianificare
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#333' }}>
                    {formatCurrency(economicsData.totale_generale.totale_costo_da_pianificare)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Tabella Riepilogo per Progetto */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Riepilogo per Progetto
            </Typography>
            <TableContainer sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={50} />
                    <TableCell sx={{ fontWeight: 'bold' }}>Progetto</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>PM</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Budget gg/uu</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Pianificato gg/uu</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Disponibile gg/uu</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: 150 }} align="center">Stato Budget</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Budget Costo Totale</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Costo totale pianificato</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Costo Disponibile</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {economicsData.riepilogo.map((row) => {
                    const isExpanded = expandedRows.has(row.id_progetto);
                    const dettagliProgetto = economicsData.dettaglio.filter(
                      d => d.id_progetto === row.id_progetto
                    );

                    return (
                      <React.Fragment key={row.id_progetto}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton size="small" onClick={() => toggleRow(row.id_progetto)}>
                              {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{row.titolo}</TableCell>
                          <TableCell>{row.cliente || '-'}</TableCell>
                          <TableCell>{row.pm || '-'}</TableCell>
                          <TableCell align="right">{row.budget_gg_uu != null ? parseFloat(row.budget_gg_uu.toString()).toFixed(1) : '0.0'} gg</TableCell>
                          <TableCell align="right">{row.budget_pianificato_gg_uu != null ? parseFloat(row.budget_pianificato_gg_uu.toString()).toFixed(1) : '0.0'} gg</TableCell>
                          <TableCell align="right" sx={{
                            fontWeight: 'bold',
                            color: row.budget_disponibile_gg_uu != null && row.budget_disponibile_gg_uu < 0 ? 'error.main' : 'inherit'
                          }}>
                            {row.budget_disponibile_gg_uu != null ? parseFloat(row.budget_disponibile_gg_uu.toString()).toFixed(1) : '0.0'} gg
                          </TableCell>
                          <TableCell align="center" sx={{ px: 2 }}>
                            {row.budget_gg_uu !== undefined && row.budget_gg_uu !== null && row.budget_gg_uu > 0 ? (
                              <BudgetProgressBarMini
                                budget_gg_uu={parseFloat(row.budget_gg_uu.toString())}
                                budget_pianificato_gg_uu={parseFloat((row.budget_pianificato_gg_uu ?? 0).toString())}
                                budget_disponibile_gg_uu={parseFloat((row.budget_disponibile_gg_uu ?? 0).toString())}
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(row.budget_costo_totale != null ? parseFloat(row.budget_costo_totale.toString()) : 0)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(row.totale_costo != null ? parseFloat(row.totale_costo.toString()) : 0)}
                          </TableCell>
                          <TableCell align="right" sx={{
                            fontWeight: 'bold',
                            color: row.costo_disponibile != null && row.costo_disponibile < 0 ? 'error.main' : 'inherit'
                          }}>
                            {formatCurrency(row.costo_disponibile != null ? parseFloat(row.costo_disponibile.toString()) : 0)}
                          </TableCell>
                        </TableRow>

                        {/* Dettaglio per risorsa/mese */}
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 2 }}>
                                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                  Dettaglio Allocazioni
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Risorsa</TableCell>
                                      <TableCell>Struttura</TableCell>
                                      <TableCell>Mese</TableCell>
                                      <TableCell align="right">Allocazione %</TableCell>
                                      <TableCell align="right">Ore</TableCell>
                                      <TableCell align="right">Giorni Uomo</TableCell>
                                      <TableCell align="right">Costo Orario</TableCell>
                                      <TableCell align="right">Costo Totale</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {dettagliProgetto.map((dettaglio, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell>{dettaglio.cognome} {dettaglio.nome}</TableCell>
                                        <TableCell>{dettaglio.struttura || '-'}</TableCell>
                                        <TableCell>
                                          <Chip label={MESI_LABELS[dettaglio.mese]} size="small" />
                                        </TableCell>
                                        <TableCell align="right">{dettaglio.percentuale}%</TableCell>
                                        <TableCell align="right">{dettaglio.ore_allocate != null ? parseFloat(dettaglio.ore_allocate.toString()).toFixed(2) : '0.00'} h</TableCell>
                                        <TableCell align="right">{dettaglio.giorni_uomo != null ? parseFloat(dettaglio.giorni_uomo.toString()).toFixed(2) : '0.00'} gg</TableCell>
                                        <TableCell align="right">{formatCurrency(dettaglio.costo_orario != null ? dettaglio.costo_orario : 0)}</TableCell>
                                        <TableCell align="right">{formatCurrency(dettaglio.costo != null ? parseFloat(dettaglio.costo.toString()) : 0)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}

                  {/* Riga Totali */}
                  <TableRow sx={{ backgroundColor: '#f5f5f5', borderTop: '2px solid #ddd' }}>
                    <TableCell />
                    <TableCell colSpan={3} sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                      TOTALI
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                      {economicsData.totale_generale.totale_budget_gg_uu.toFixed(1)} gg
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                      {economicsData.totale_generale.totale_giorni_uomo.toFixed(1)} gg
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                      {economicsData.totale_generale.totale_gg_uu_da_pianificare.toFixed(1)} gg
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                      -
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: '#f5576c' }}>
                      {formatCurrency(economicsData.totale_generale.totale_budget_costo)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: '#f5576c' }}>
                      {formatCurrency(economicsData.totale_generale.totale_costo)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: '#f5576c' }}>
                      {formatCurrency(economicsData.totale_generale.totale_costo_da_pianificare)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default EconomicsPage;
